/**
 * Professional Chat Widget Styles
 * Fully customizable via CSS variables
 */
export const widgetStyles = `
  :host {
    /* Colors */
    --chat-primary-color: #6366f1;
    --chat-primary-hover: #4f46e5;
    --chat-bg-color: #ffffff;
    --chat-text-color: #1f2937;
    --chat-text-secondary: #6b7280;
    --chat-border-color: #e5e7eb;
    --chat-input-bg: #f9fafb;
    --chat-bot-bg: #f3f4f6;
    --chat-user-bg: var(--chat-primary-color);
    --chat-user-text: #ffffff;

    /* Dimensions */
    --chat-width: 380px;
    --chat-height: 600px;
    --chat-button-size: 60px;
    --chat-radius: 16px;
    --chat-button-radius: 50%;
    --chat-header-height: 70px;

    /* Typography */
    --chat-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    --chat-font-size: 14px;

    /* Spacing */
    --chat-button-offset-x: 24px;
    --chat-button-offset-y: 24px;

    /* Effects */
    --chat-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    --chat-shadow-hover: 0 15px 50px rgba(0, 0, 0, 0.2);

    all: initial;
    font-family: var(--chat-font);
    font-size: var(--chat-font-size);
    line-height: 1.5;
    color: var(--chat-text-color);
  }

  :host(.theme-dark) {
    --chat-bg-color: #1f2937;
    --chat-text-color: #f9fafb;
    --chat-text-secondary: #9ca3af;
    --chat-border-color: #374151;
    --chat-input-bg: #374151;
    --chat-bot-bg: #374151;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Container */
  .chat-widget-container {
    position: fixed;
    z-index: 2147483647;
    font-family: var(--chat-font);
  }

  .chat-widget-container.position-bottom-right {
    bottom: var(--chat-button-offset-y);
    right: var(--chat-button-offset-x);
  }

  .chat-widget-container.position-bottom-left {
    bottom: var(--chat-button-offset-y);
    left: var(--chat-button-offset-x);
  }

  /* Embedded mode - for preview containers */
  :host(.embedded) {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }

  .chat-widget-container.embedded {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .chat-widget-container.embedded .chat-toggle-btn {
    position: absolute;
    bottom: 16px;
    right: 16px;
  }

  .chat-widget-container.embedded.position-bottom-left .chat-toggle-btn {
    right: auto;
    left: 16px;
  }

  .chat-widget-container.embedded .chat-panel {
    position: absolute;
    max-height: calc(100% - 90px);
    max-width: calc(100% - 32px);
    bottom: 80px;
  }

  .chat-widget-container.embedded .chat-panel.expanded {
    max-height: calc(100% - 40px);
    max-width: calc(100% - 32px);
    width: calc(100% - 32px);
    height: calc(100% - 90px);
  }

  .chat-widget-container.embedded.position-bottom-right .chat-panel {
    right: 16px;
  }

  .chat-widget-container.embedded.position-bottom-left .chat-panel {
    left: 16px;
  }

  /* ========== Toggle Button ========== */
  .chat-toggle-btn {
    width: var(--chat-button-size);
    height: var(--chat-button-size);
    border-radius: var(--chat-button-radius);
    border: none;
    background: var(--chat-primary-color);
    color: white;
    cursor: pointer;
    box-shadow: var(--chat-shadow);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
  }

  .chat-toggle-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%);
    border-radius: inherit;
  }

  .chat-toggle-btn:hover {
    transform: scale(1.08);
    box-shadow: var(--chat-shadow-hover);
    background: var(--chat-primary-hover);
  }

  .chat-toggle-btn:focus {
    outline: none;
    box-shadow: var(--chat-shadow-hover), 0 0 0 3px rgba(99, 102, 241, 0.3);
  }

  .chat-toggle-btn:active {
    transform: scale(1.02);
  }

  .chat-toggle-btn svg,
  .chat-toggle-btn .btn-icon {
    width: 26px;
    height: 26px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .chat-toggle-btn.open svg,
  .chat-toggle-btn.open .btn-icon {
    transform: rotate(0deg);
  }

  .chat-toggle-btn .btn-emoji {
    font-size: 28px;
    line-height: 1;
  }

  /* Notification badge */
  .chat-toggle-btn .notification-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 20px;
    height: 20px;
    background: #ef4444;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--chat-bg-color);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  /* ========== Chat Panel ========== */
  .chat-panel {
    position: absolute;
    bottom: calc(var(--chat-button-size) + 16px);
    width: var(--chat-width);
    height: var(--chat-height);
    max-height: calc(100vh - var(--chat-button-size) - 60px);
    background: var(--chat-bg-color);
    border-radius: var(--chat-radius);
    box-shadow: var(--chat-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    opacity: 0;
    transform: translateY(20px) scale(0.95);
    transform-origin: bottom right;
    pointer-events: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid var(--chat-border-color);
  }

  .position-bottom-right .chat-panel {
    right: 0;
    transform-origin: bottom right;
  }

  .position-bottom-left .chat-panel {
    left: 0;
    transform-origin: bottom left;
  }

  .chat-panel.open {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  /* Expanded mode */
  .chat-panel.expanded {
    width: calc(var(--chat-width) * 1.5);
    height: calc(var(--chat-height) * 1.3);
    max-width: calc(100vw - 48px);
    max-height: calc(100vh - 100px);
  }

  /* ========== Header ========== */
  .chat-header {
    min-height: var(--chat-header-height);
    padding: 16px 20px;
    background: var(--chat-primary-color);
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
  }

  .chat-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
  }

  .chat-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 1;
  }

  .chat-header-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .chat-header-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .chat-header-info h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    line-height: 1.3;
  }

  .chat-header-info p {
    font-size: 12px;
    opacity: 0.9;
    margin: 2px 0 0;
    line-height: 1.3;
  }

  .chat-header-actions {
    display: flex;
    gap: 8px;
    position: relative;
    z-index: 1;
  }

  .chat-close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }

  .chat-close-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }

  .chat-close-btn svg {
    width: 18px;
    height: 18px;
  }

  .chat-reset-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }

  .chat-reset-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }

  .chat-reset-btn svg {
    width: 16px;
    height: 16px;
  }

  .chat-expand-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }

  .chat-expand-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }

  .chat-expand-btn svg {
    width: 16px;
    height: 16px;
  }

  /* ========== Messages Area ========== */
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    scroll-behavior: smooth;
  }

  .chat-messages::-webkit-scrollbar {
    width: 6px;
  }

  .chat-messages::-webkit-scrollbar-track {
    background: transparent;
  }

  .chat-messages::-webkit-scrollbar-thumb {
    background: var(--chat-border-color);
    border-radius: 3px;
  }

  .chat-messages::-webkit-scrollbar-thumb:hover {
    background: var(--chat-text-secondary);
  }

  /* Message Container */
  .chat-message-container {
    display: flex;
    gap: 10px;
    animation: messageIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .chat-message-container.user {
    flex-direction: row-reverse;
  }

  @keyframes messageIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Message Avatar */
  .chat-message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--chat-bot-bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .chat-message-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Message Content */
  .chat-message-content {
    max-width: 75%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .chat-message-container.user .chat-message-content {
    align-items: flex-end;
  }

  .chat-message-sender {
    font-size: 12px;
    font-weight: 500;
    color: var(--chat-text-secondary);
    padding: 0 4px;
  }

  .chat-message {
    padding: 12px 16px;
    border-radius: 16px;
    word-wrap: break-word;
    line-height: 1.5;
    position: relative;
  }

  .chat-message.bot {
    background: var(--chat-bot-bg);
    color: var(--chat-text-color);
    border-bottom-left-radius: 4px;
  }

  .chat-message.user {
    background: var(--chat-user-bg);
    color: var(--chat-user-text);
    border-bottom-right-radius: 4px;
  }

  .chat-message-time {
    font-size: 10px;
    color: var(--chat-text-secondary);
    padding: 0 4px;
  }

  .chat-message-container.user .chat-message-time {
    text-align: right;
  }

  /* Markdown Styles */
  .chat-message p {
    margin: 0 0 8px;
  }

  .chat-message p:last-child {
    margin-bottom: 0;
  }

  .chat-message strong {
    font-weight: 600;
  }

  .chat-message em {
    font-style: italic;
  }

  .chat-message code {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', Monaco, 'Consolas', monospace;
    font-size: 13px;
  }

  .chat-message.user code {
    background: rgba(255, 255, 255, 0.2);
  }

  .chat-message pre {
    background: rgba(0, 0, 0, 0.1);
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;
  }

  .chat-message.user pre {
    background: rgba(255, 255, 255, 0.2);
  }

  .chat-message pre code {
    background: transparent;
    padding: 0;
  }

  .chat-message ul,
  .chat-message ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  .chat-message li {
    margin: 4px 0;
  }

  .chat-message a {
    color: inherit;
    text-decoration: underline;
  }

  .chat-message.bot a {
    color: var(--chat-primary-color);
  }

  /* ========== Typing Indicator ========== */
  .typing-indicator {
    display: flex;
    gap: 5px;
    padding: 12px 16px;
    background: var(--chat-bot-bg);
    border-radius: 16px;
    border-bottom-left-radius: 4px;
    align-self: flex-start;
    width: fit-content;
  }

  .typing-indicator span {
    width: 8px;
    height: 8px;
    background: var(--chat-text-secondary);
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
  }

  .typing-indicator span:nth-child(1) {
    animation-delay: 0s;
  }

  .typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-6px);
      opacity: 1;
    }
  }

  /* ========== Input Area ========== */
  .chat-input-container {
    padding: 16px 20px;
    border-top: 1px solid var(--chat-border-color);
    display: flex;
    gap: 12px;
    background: var(--chat-bg-color);
    flex-shrink: 0;
    align-items: flex-end;
  }

  .chat-input-wrapper {
    flex: 1;
    position: relative;
  }

  .chat-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid var(--chat-border-color);
    border-radius: 24px;
    background: var(--chat-input-bg);
    color: var(--chat-text-color);
    font-size: var(--chat-font-size);
    font-family: inherit;
    resize: none;
    outline: none;
    transition: all 0.2s ease;
    max-height: 120px;
    line-height: 1.5;
  }

  .chat-input:focus {
    border-color: var(--chat-primary-color);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  .chat-input::placeholder {
    color: var(--chat-text-secondary);
  }

  .chat-send-btn {
    width: 44px;
    height: 44px;
    border: none;
    background: var(--chat-primary-color);
    color: white;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .chat-send-btn:hover:not(:disabled) {
    background: var(--chat-primary-hover);
    transform: scale(1.05);
  }

  .chat-send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .chat-send-btn svg {
    width: 20px;
    height: 20px;
  }

  /* ========== Powered By Footer ========== */
  .chat-powered-by {
    padding: 8px 20px 12px;
    text-align: center;
    font-size: 11px;
    color: var(--chat-text-secondary);
    background: var(--chat-bg-color);
    border-top: 1px solid var(--chat-border-color);
  }

  .chat-powered-by a {
    color: var(--chat-primary-color);
    text-decoration: none;
    font-weight: 500;
    transition: opacity 0.2s;
  }

  .chat-powered-by a:hover {
    opacity: 0.8;
  }

  /* ========== Mobile Responsive ========== */
  @media (max-width: 480px) {
    .chat-panel {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      max-height: none;
      border-radius: 0;
      transform: translateY(100%);
    }

    .chat-panel.open {
      transform: translateY(0);
    }

    .chat-header {
      border-radius: 0;
    }

    .chat-toggle-btn {
      position: fixed;
      bottom: var(--chat-button-offset-y);
      right: var(--chat-button-offset-x);
    }

    .position-bottom-left .chat-toggle-btn {
      right: auto;
      left: var(--chat-button-offset-x);
    }
  }

  /* ========== Welcome Screen ========== */
  .chat-welcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    flex: 1;
  }

  .chat-welcome-icon {
    width: 64px;
    height: 64px;
    background: var(--chat-primary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    font-size: 32px;
    color: white;
  }

  .chat-welcome h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--chat-text-color);
  }

  .chat-welcome p {
    font-size: 14px;
    color: var(--chat-text-secondary);
    max-width: 280px;
  }

  /* ========== Empty State ========== */
  .chat-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    color: var(--chat-text-secondary);
  }

  .chat-empty-state svg {
    width: 48px;
    height: 48px;
    opacity: 0.5;
    margin-bottom: 12px;
  }

  /* ========== Initial Prompts ========== */
  .chat-initial-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 20px 16px;
    animation: promptsIn 0.4s ease;
  }

  @keyframes promptsIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .chat-prompt-btn {
    padding: 10px 16px;
    background: var(--chat-bg-color);
    border: 1px solid var(--chat-border-color);
    border-radius: 20px;
    color: var(--chat-text-color);
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    line-height: 1.4;
  }

  .chat-prompt-btn:hover {
    background: var(--chat-primary-color);
    border-color: var(--chat-primary-color);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  .chat-prompt-btn:active {
    transform: translateY(0);
  }

  .chat-prompt-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }
`;
